import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
} from '@angular/core';
import { scaleLinear } from 'd3-scale';
import { clamp } from 'lodash';

function toSignificantDigits(number: number, digits: number): number {
  return Number(number.toFixed(digits));
}

@Component({
  selector: 'range-slider',
  templateUrl: './range-slider.component.html',
  styleUrls: ['./range-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RangeSliderComponent {
  @Input()
  lowerMin: number = 0;

  @Input()
  lowerMax: number = 0;

  @Input()
  upperMin: number = 0;

  @Input()
  upperMax: number = 0;

  private _value: [number, number] = [0, 0];
  @Input()
  set value(v: [number, number]) {
    if (v[0] === null || v[1] === null) return;
    const initialClamp = [
      clamp(v[0], this.lowerMin, this.lowerMax),
      clamp(v[1], this.upperMin, this.upperMax),
    ];
    const fullyClamped = [
      clamp(initialClamp[0], this.lowerMin, initialClamp[1]),
      clamp(initialClamp[1], initialClamp[0], this.upperMax),
    ] as [number, number];
    this._value = fullyClamped;
    this.valueChange.emit(this._value);
  }
  get value(): [number, number] {
    return this._value;
  }

  @Input()
  type: 'number' | 'currency' | 'percent' = 'number';

  @Input()
  dragSignificantDigits: number = 2;

  trackWidth: number;

  getScale() {
    return scaleLinear()
      .domain([this.lowerMin, this.upperMax])
      .range([0, 100])
      .clamp(true);
  }

  getLowerInRange() {
    const scale = this.getScale();
    return scale(this.value[0]);
  }

  getUpperInRange() {
    const scale = this.getScale();
    return scale(this.value[1]);
  }

  getWidthInRange() {
    return this.getUpperInRange()! - this.getLowerInRange()!;
  }

  getEffectiveLowerMax() {
    return Math.min(this.lowerMax, this.value[1]);
  }

  getEffectiveUpperMin() {
    return Math.max(this.upperMin, this.value[0]);
  }

  getInverseDiffScale() {
    return scaleLinear()
      .domain([0, this.trackWidth])
      .range([0, this.upperMax - this.lowerMin]);
  }

  lowerDragStart: [number, number] | null = null;
  upperDragStart: [number, number] | null = null;

  @HostListener('document:mousemove', ['$event.clientX', '$event'])
  onMouseMove(clientX: number, evt: MouseEvent) {
    if (this.lowerDragStart) {
      const scale = this.getInverseDiffScale();
      const diff = scale(clientX - this.lowerDragStart[1]);
      this.value = [
        toSignificantDigits(
          this.lowerDragStart[0] + diff!,
          this.dragSignificantDigits,
        ),
        this.value[1],
      ];
      evt.preventDefault();
    }
    if (this.upperDragStart) {
      const scale = this.getInverseDiffScale();
      const diff = scale(clientX - this.upperDragStart[1]);
      this.value = [
        this.value[0],
        toSignificantDigits(
          this.upperDragStart[0] + diff!,
          this.dragSignificantDigits,
        ),
      ];
      evt.preventDefault();
    }
  }

  @HostListener('document:mouseup')
  onMouseUp() {
    this.lowerDragStart = null;
    this.upperDragStart = null;
  }

  @Output()
  valueChange = new EventEmitter<[number, number]>();
}
